import { useState, useEffect } from 'react';
import { MapPin, Trash2, Clock, ExternalLink } from 'lucide-react';
import { useT } from '@/lib/i18n';
import { Link, useSearchParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { ScoreRow } from '@/components/ui/score-row';
import { SavedAssignmentSheet } from '@/components/assignments/saved-assignment-sheet';
import { formatDate } from '@/lib/format';
import {
  useSavedAssignments,
  useUpdateSavedAssignment,
  useRemoveSavedAssignment,
  useSaveAssignment
} from '@/hooks/use-saved-assignments';
import type { SavedAssignment } from '@/hooks/use-saved-assignments';

type AssignmentFilter = 'all' | 'active' | 'closed';

const statusConfig: Record<string, { label: string; color: string }> = {
  not_applied: {
    label: 'Saved',
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300'
  },
  applied: {
    label: 'Applied',
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
  },
  won: {
    label: 'Won',
    color: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
  },
  lost: { label: 'Lost', color: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400' }
};

function StatusButtons({
  saved,
  onUpdate
}: {
  saved: SavedAssignment;
  onUpdate: (status: string) => void;
}) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {saved.status === 'not_applied' && (
        <Button
          size="sm"
          variant="outline"
          className="text-xs"
          onClick={(e) => {
            e.stopPropagation();
            onUpdate('applied');
          }}
        >
          Mark Applied
        </Button>
      )}
      {saved.status === 'applied' && (
        <>
          <Button
            size="sm"
            className="bg-green-600 text-xs hover:bg-green-700"
            onClick={(e) => {
              e.stopPropagation();
              onUpdate('won');
            }}
          >
            Won
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="text-xs text-red-600 dark:text-red-400"
            onClick={(e) => {
              e.stopPropagation();
              onUpdate('lost');
            }}
          >
            Lost
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="text-xs text-muted-foreground"
            onClick={(e) => {
              e.stopPropagation();
              onUpdate('not_applied');
            }}
          >
            Undo
          </Button>
        </>
      )}
    </div>
  );
}

const filterOptions: Array<{ value: AssignmentFilter; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'active', label: 'Active' },
  { value: 'closed', label: 'Closed' }
];

export function MyAssignmentsPage() {
  const t = useT();
  const [searchParams, setSearchParams] = useSearchParams();
  const { data, isLoading } = useSavedAssignments();
  const updateStatus = useUpdateSavedAssignment();
  const remove = useRemoveSavedAssignment();
  const saveAssignment = useSaveAssignment();
  const [filter, setFilter] = useState<AssignmentFilter>('all');
  const [selectedSaved, setSelectedSaved] = useState<SavedAssignment | null>(null);

  const allSaved = data?.data ?? [];

  // Handle ?assignment=<id> query param — auto-open the detail sheet
  useEffect(() => {
    const assignmentId = searchParams.get('assignment');
    if (!assignmentId || isLoading) return;

    const existing = allSaved.find((s) => s.assignmentId === assignmentId);
    if (existing) {
      setSelectedSaved(existing);
      setSearchParams({}, { replace: true });
    } else {
      // Not saved yet — save it, then open when data refreshes
      saveAssignment.mutate(
        { assignmentId },
        {
          onSuccess: () => {
            setSearchParams({}, { replace: true });
          }
        }
      );
    }
  }, [searchParams, isLoading, allSaved]);

  // Auto-select newly saved assignment when data arrives after save
  useEffect(() => {
    const assignmentId = searchParams.get('assignment');
    if (!assignmentId) return;
    const existing = allSaved.find((s) => s.assignmentId === assignmentId && !selectedSaved);
    if (existing) {
      setSelectedSaved(existing);
      setSearchParams({}, { replace: true });
    }
  }, [allSaved, selectedSaved]);

  const saved =
    filter === 'all'
      ? allSaved
      : allSaved.filter((s) =>
          filter === 'active' ? s.assignmentStatus !== 'closed' : s.assignmentStatus === 'closed'
        );

  const groups = {
    applied: saved.filter((s) => s.status === 'applied'),
    not_applied: saved.filter((s) => s.status === 'not_applied'),
    won: saved.filter((s) => s.status === 'won'),
    lost: saved.filter((s) => s.status === 'lost')
  };

  const groupOrder: Array<{ key: string; label: string; items: SavedAssignment[] }> = [
    { key: 'applied', label: 'Applied', items: groups.applied },
    { key: 'not_applied', label: 'Saved', items: groups.not_applied },
    { key: 'won', label: 'Won', items: groups.won },
    { key: 'lost', label: 'Lost', items: groups.lost }
  ].filter((g) => g.items.length > 0);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-semibold">{t('myAssignments.title')}</h1>
        <div className="flex items-center gap-3">
          <div className="flex rounded-md border border-border">
            {filterOptions.map((opt) => (
              <button
                key={opt.value}
                onClick={() => setFilter(opt.value)}
                className={`px-3 py-1.5 text-xs font-medium transition-colors first:rounded-l-md last:rounded-r-md ${
                  filter === opt.value
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
          <p className="text-xs text-muted-foreground">
            {saved.length}/{allSaved.length}
          </p>
        </div>
      </div>

      {isLoading ? (
        <div className="py-12 text-center text-sm text-muted-foreground">
          {t('myAssignments.loading')}
        </div>
      ) : saved.length === 0 ? (
        <div className="rounded-lg border border-dashed p-12 text-center">
          <h2 className="text-lg font-semibold">{t('myAssignments.empty')}</h2>
          <p className="mt-2 text-sm text-muted-foreground">{t('myAssignments.emptyHint')}</p>
        </div>
      ) : (
        <div className="space-y-6">
          {groupOrder.map((group) => (
            <div key={group.key}>
              <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
                <Badge variant="outline" className={statusConfig[group.key]?.color ?? ''}>
                  {group.label}
                </Badge>
                <span className="text-muted-foreground">({group.items.length})</span>
              </h2>
              <div className="space-y-2">
                {group.items.map((item) => (
                  <Card
                    key={item.id}
                    className="cursor-pointer transition-colors hover:bg-muted/30"
                    onClick={() => setSelectedSaved(item)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold">{item.assignmentTitle}</h3>
                            {item.assignmentStatus === 'closed' && (
                              <Badge
                                variant="outline"
                                className="bg-gray-100 text-gray-500 dark:bg-gray-800 dark:text-gray-400 text-[10px]"
                              >
                                Closed
                              </Badge>
                            )}
                          </div>
                          <div className="mt-1 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                            {item.assignmentLocation && (
                              <span className="flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {item.assignmentLocation}
                              </span>
                            )}
                            {item.appliedDate && (
                              <span className="flex items-center gap-1">
                                <Clock className="h-3 w-3" />
                                Applied {formatDate(item.appliedDate)}
                              </span>
                            )}
                            <span>Saved {formatDate(item.createdAt)}</span>
                          </div>

                          {/* Notes */}
                          <div className="mt-2">
                            <input
                              type="text"
                              placeholder="Add a note..."
                              defaultValue={item.notes ?? ''}
                              onBlur={(e) => {
                                const newNotes = e.target.value;
                                if (newNotes !== (item.notes ?? '')) {
                                  updateStatus.mutate({ id: item.id, notes: newNotes });
                                }
                              }}
                              className="w-full rounded-md border bg-background px-2 py-1 text-xs outline-none focus:ring-1 focus:ring-ring"
                              onClick={(e) => e.stopPropagation()}
                            />
                          </div>

                          {/* Match scores */}
                          {item.matchScore && (
                            <div className="mt-2 flex items-center gap-3">
                              <ScoreRow
                                aiScore={item.matchScore}
                                heuristicScore={item.heuristicScore ?? null}
                                teamFitScore={item.teamFitScore ?? null}
                                layout="inline"
                              />
                              {item.matchId && (
                                <Link
                                  to="/matches"
                                  className="flex items-center gap-1 text-[10px] text-primary hover:underline"
                                >
                                  <ExternalLink className="h-3 w-3" />
                                  View match
                                </Link>
                              )}
                            </div>
                          )}
                        </div>

                        <div className="ml-4 flex items-center gap-2">
                          <StatusButtons
                            saved={item}
                            onUpdate={(status) => updateStatus.mutate({ id: item.id, status })}
                          />
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={(e) => {
                              e.stopPropagation();
                              remove.mutate(item.id);
                            }}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-red-500 dark:text-red-400" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      <SavedAssignmentSheet
        saved={selectedSaved}
        open={!!selectedSaved}
        onOpenChange={(open) => {
          if (!open) setSelectedSaved(null);
        }}
      />
    </div>
  );
}
